"use client";

import "./pharma-lifescience.css";
import { useEffect } from "react";

/* =========================================================
   DATA
========================================================= */

const questions = [
  "Which batches are at risk of expiry?",
  "Where is inventory building faster than demand?",
  "Which products are growing but diluting margin?",
  "Which materials could disrupt production?",
  "Why is batch yield below expectation?",
  "Where are quality exceptions recurring?",
  "Which distributors/territories are underperforming?",
  "Which products may stock out?",
  "Where is working capital trapped?",
  "Can every batch and transaction be traced when required?",
];

const intelligenceAreas = [
  {
    title: "Product & Portfolio",
    items: "Product • Brand\nFormulation • Pack\nPricing • Margin",
    icon: "chart",
  },
  {
    title: "Batch & Quality",
    items: "Batch • Yield\nDeviation • Release\nStability • CAPA",
    icon: "flask",
  },
  {
    title: "Supply & Manufacturing",
    items: "Material • Capacity\nProduction • Planning\nAvailability • Co-manufacturing",
    icon: "gear",
  },
  {
    title: "Inventory & Expiry",
    items: "Batch Stock • Shelf Life\nAgeing • Expiry Risk\nReplenishment",
    icon: "box",
  },
  {
    title: "Distribution & Commercial",
    items: "Distributor • Territory\nCustomer • Sales\nReturns • Trade Schemes",
    icon: "truck",
  },
  {
    title: "Compliance & Traceability",
    items: "Batch Genealogy • Documents\nRegulatory • Audits\nControls • Evidence",
    icon: "shield",
  },
];

const valueChainSteps = [
  {
    title: "Research & Development",
    description: "Turn science into validated molecules and formulations.",
    icon: "research",
    color: "#2F6FED",
  },
  {
    title: "Product Development",
    description: "Shape the right product and pack for market needs.",
    icon: "product",
    color: "#8B5CF6",
  },
  {
    title: "Manufacturing",
    description: "Convert batches and processes into consistent quality output.",
    icon: "manufacture",
    color: "#F5B120",
  },
  {
    title: "Quality & Release",
    description: "Ensure every batch meets compliance before it ships.",
    icon: "quality",
    color: "#22C55E",
  },
  {
    title: "Inventory & Expiry",
    description: "Track shelf life and warehouse stock in real time.",
    icon: "inventory",
    color: "#EC4899",
  },
  {
    title: "Distribution",
    description: "Get the right stock to every stockist and distributor.",
    icon: "truck",
    color: "#06B6D4",
  },
  {
    title: "Market & Customers",
    description: "Reach hospitals, retail and e-commerce reliably.",
    icon: "store",
    color: "#F97316",
  },
  {
    title: "Patients & Consumers",
    description: "Deliver healthier outcomes to the people who need them.",
    icon: "people",
    color: "#EF4444",
  },
];

/* "Batch at the Heart of Everything" diagram: 4 steps sit left of the hub,
   4 sit right. A plain CSS Grid (no fixed pixel math) so it stays correctly
   divided and centered at any viewport width. */
const radialLeftSteps = valueChainSteps.filter((_, i) => i % 2 === 0);
const radialRightSteps = valueChainSteps.filter((_, i) => i % 2 === 1);

const systems = [
  { name: "SAP", icon: "database" },
  { name: "ERP", icon: "layers" },
  { name: "LMS / QMS", icon: "quality" },
  { name: "MES", icon: "manufacture" },
  { name: "Planning", icon: "chart" },
  { name: "DMS", icon: "store" },
  { name: "CRM", icon: "people" },
  { name: "Regulatory / Documents", icon: "file" },
];

const timeDecisions = [
  ["Formulate", "What should we develop?", "flask"],
  ["Manufacture", "How much should we produce?", "manufacture"],
  ["Release", "Does this batch pass quality?", "quality"],
  ["Distribute", "Where should stock go first?", "truck"],
  ["Monitor Expiry", "What needs to move now?", "inventory"],
  ["Replenish", "When and how much to reorder?", "chart"],
];

/* =========================================================
   SVG ICON SYSTEM
========================================================= */

const svgProps = {
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.8,
  strokeLinecap: "round",
  strokeLinejoin: "round",
};

const IconResearch = () => (
  <svg {...svgProps}>
    <path d="M9 2h6" />
    <path d="M10 2v6l-5 10a3 3 0 0 0 2.7 4h8.6A3 3 0 0 0 19 18L14 8V2" />
    <path d="M7 16h10" />
  </svg>
);

const IconProduct = () => (
  <svg {...svgProps}>
    <path d="m12 3 8 4.5-8 4.5-8-4.5L12 3Z" />
    <path d="M4 12v5l8 4 8-4v-5" />
    <path d="M12 12v9" />
  </svg>
);

const IconManufacture = () => (
  <svg {...svgProps}>
    <path d="M3 21V9l7 4V9l7 4V5h4v16" />
    <path d="M3 21h18" />
    <path d="M7 17h2M12 17h2M17 17h2" />
  </svg>
);

const IconQuality = () => (
  <svg {...svgProps}>
    <circle cx="12" cy="12" r="9" />
    <path d="m8 12 2.5 2.5L16 9" />
  </svg>
);

const IconInventory = () => (
  <svg {...svgProps}>
    <path d="M4 8h16v13H4z" />
    <path d="M7 8V5h10v3" />
    <path d="M8 12h8M8 16h5" />
  </svg>
);

const IconTruck = () => (
  <svg {...svgProps}>
    <path d="M3 6h11v10H3z" />
    <path d="M14 10h4l3 3v3h-7z" />
    <circle cx="7" cy="18" r="2" />
    <circle cx="18" cy="18" r="2" />
  </svg>
);

const IconStore = () => (
  <svg {...svgProps}>
    <path d="M3 10l2-6h14l2 6" />
    <path d="M4 10v10h16V10" />
    <path d="M4 10a2 2 0 0 0 4 0 2 2 0 0 0 4 0 2 2 0 0 0 4 0 2 2 0 0 0 4 0" />
    <path d="M9 20v-6h6v6" />
  </svg>
);

const IconPeople = () => (
  <svg {...svgProps}>
    <circle cx="9" cy="8" r="3" />
    <path d="M3 20a6 6 0 0 1 12 0" />
    <circle cx="17" cy="9" r="2.5" />
    <path d="M16 14a5 5 0 0 1 5 5" />
  </svg>
);

const IconCart = () => (
  <svg {...svgProps}>
    <circle cx="9" cy="20" r="1" fill="currentColor" stroke="none" />
    <circle cx="18" cy="20" r="1" fill="currentColor" stroke="none" />
    <path d="M2 3h2l2.4 12.2a2 2 0 0 0 2 1.6h8.2a2 2 0 0 0 2-1.6L21 7H6" />
  </svg>
);

const IconTag = () => (
  <svg {...svgProps}>
    <path d="M12 2h7a1 1 0 0 1 1 1v7a2 2 0 0 1-.6 1.4l-8 8a2 2 0 0 1-2.8 0l-6-6a2 2 0 0 1 0-2.8l8-8A2 2 0 0 1 12 2z" />
    <circle cx="15.5" cy="8.5" r="1.5" fill="currentColor" stroke="none" />
  </svg>
);

const IconChart = () => (
  <svg {...svgProps}>
    <path d="M4 20V11M10 20V6M16 20V9M22 20V3" />
  </svg>
);

const IconFlask = () => (
  <svg {...svgProps}>
    <path d="M9 3h6M10 3v6l-5 9a2 2 0 0 0 1.8 3h10.4a2 2 0 0 0 1.8-3l-5-9V3" />
    <path d="M7 15h10" />
  </svg>
);

const IconGear = () => (
  <svg {...svgProps}>
    <circle cx="12" cy="12" r="3" />
    <path d="m19.4 15 .1.1a2 2 0 1 1-2.8 2.8l-.1-.1a2 2 0 0 0-3.4 1.4v.2a2 2 0 1 1-4 0v-.2A2 2 0 0 0 5.8 18l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1A2 2 0 0 0 1.6 12.8h-.2a2 2 0 1 1 0-4h.2A2 2 0 0 0 3 5.4l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1A2 2 0 0 0 9.2 1.2V1a2 2 0 1 1 4 0v.2A2 2 0 0 0 16.6 3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a2 2 0 0 0 1.4 3.4h.2a2 2 0 1 1 0 4h-.2a2 2 0 0 0-1.4 3.4Z" />
  </svg>
);

const IconBox = () => (
  <svg {...svgProps}>
    <path d="m12 3 9 5-9 5-9-5 9-5Z" />
    <path d="M3 8v8l9 5 9-5V8" />
    <path d="M12 13v8" />
  </svg>
);

const IconShield = () => (
  <svg {...svgProps}>
    <path d="M12 3 20 6v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6l8-3Z" />
    <path d="m8.5 12 2.2 2.2 4.8-5" />
  </svg>
);

const IconDatabase = () => (
  <svg {...svgProps}>
    <ellipse cx="12" cy="5" rx="8" ry="3" />
    <path d="M4 5v14c0 1.7 3.6 3 8 3s8-1.3 8-3V5" />
    <path d="M4 12c0 1.7 3.6 3 8 3s8-1.3 8-3" />
  </svg>
);

const IconLayers = () => (
  <svg {...svgProps}>
    <path d="m12 3-9 5 9 5 9-5-9-5Z" />
    <path d="m3 13 9 5 9-5M3 17l9 5 9-5" />
  </svg>
);

const IconFile = () => (
  <svg {...svgProps}>
    <path d="M6 3h9l4 4v14H6z" />
    <path d="M15 3v5h5M9 13h6M9 17h6" />
  </svg>
);

const IconHeart = () => (
  <svg {...svgProps}>
    <path d="M20.8 8.8c0 5-8.8 10.2-8.8 10.2S3.2 13.8 3.2 8.8A4.8 4.8 0 0 1 12 6a4.8 4.8 0 0 1 8.8 2.8Z" />
  </svg>
);

const IconArrowRight = () => (
  <svg {...svgProps}>
    <path d="M4 12h15" />
    <path d="M13 6l6 6-6 6" />
  </svg>
);

const IconArrowDown = () => (
  <svg {...svgProps}>
    <path d="M12 4v15" />
    <path d="m6 13 6 6 6-6" />
  </svg>
);

const ICONS = {
  research: IconResearch,
  product: IconProduct,
  manufacture: IconManufacture,
  quality: IconQuality,
  inventory: IconInventory,
  truck: IconTruck,
  store: IconStore,
  people: IconPeople,
  cart: IconCart,
  tag: IconTag,
  chart: IconChart,
  flask: IconFlask,
  gear: IconGear,
  box: IconBox,
  shield: IconShield,
  database: IconDatabase,
  layers: IconLayers,
  file: IconFile,
  heart: IconHeart,
  arrowRight: IconArrowRight,
  arrowDown: IconArrowDown,
};

function Icon({ type, className = "" }) {
  const Component = ICONS[type];
  if (!Component) return null;

  return (
    <span className={`custom-icon ${className}`} aria-hidden="true">
      <Component />
    </span>
  );
}

/* =========================================================
   COMPONENT
========================================================= */

export default function PharmaLifescience() {
  useEffect(() => {
    const elements = document.querySelectorAll(
      ".pharma-page .reveal-item, .pharma-page .reveal-card"
    );

    if (!elements.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("in-view");
            observer.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.12,
        rootMargin: "0px 0px -30px 0px",
      }
    );

    elements.forEach((element) => observer.observe(element));

    return () => observer.disconnect();
  }, []);

  return (
    <main className="pharma-page">
      {/* =====================================================
          SECTION 01 — HERO
      ===================================================== */}
      <section className="fashion-hero">
        <div className="hero-overlay" />

        <div className="hero-content">
          <div className="hero-left">
            <h1>
              From Molecule to Market.
              <br />
              From Batch to Business Value.
            </h1>

            <p className="hero-description">
              Orane connects product, batch, quality, manufacturing,
              inventory, distribution, sales and compliance data to help
              pharma (Rx, generics) and wellness enterprises make faster,
              safer and more profitable decisions.
            </p>

            <div className="hero-buttons">
              <a href="/tellusyourproblem#tell-details" className="btn btn-primary">
                Tell Us Your Problem
                <Icon type="arrowRight" />
              </a>

              <a href="#value-chain" className="btn btn-outline">
                See How It Works
                <span className="play-icon">▶</span>
              </a>
            </div>
          </div>

          <div className="hero-stats">
            <div className="hero-stat">
              <div className="hero-stat-icon">
                <Icon type="quality" />
              </div>
              <span>
                Quality
                <br />
                Assurance
              </span>
            </div>

            <div className="hero-stat">
              <div className="hero-stat-icon">
                <Icon type="shield" />
              </div>
              <span>
                Regulatory
                <br />
                Compliance
              </span>
            </div>

            <div className="hero-stat">
              <div className="hero-stat-icon">
                <Icon type="database" />
              </div>
              <span>
                Batch
                <br />
                Traceability
              </span>
            </div>

            <div className="hero-stat">
              <div className="hero-stat-icon">
                <Icon type="box" />
              </div>
              <span>
                Higher
                <br />
                Availability
              </span>
            </div>

            <div className="hero-stat">
              <div className="hero-stat-icon">
                <Icon type="heart" />
              </div>
              <span>
                Healthier
                <br />
                Lives
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* =====================================================
          SECTION 02A — QUESTIONS
      ===================================================== */}
      <section className="fashion-section questions-section">
        <div className="fashion-section-inner">
          <div className="questions-card reveal-item">
            <div className="questions-content">
              <div className="section-heading">
                <h2>The Questions Pharma Leaders Ask</h2>
                <p>Real questions. Real decisions. Real impact.</p>
              </div>

              <div className="questions-list">
                {questions.map((question, index) => (
                  <div
                    className="question-item reveal-item"
                    key={question}
                    style={{ "--delay": `${index * 45}ms` }}
                  >
                    <span className="question-number">{index + 1}</span>
                    <p>{question}</p>
                    <span className="question-arrow">
                      <Icon type="arrowRight" />
                    </span>
                  </div>
                ))}
              </div>

              <a href="#contact" className="questions-more">
                View All Questions
                <Icon type="arrowRight" />
              </a>
            </div>

            <div className="questions-image">
              <img src="/assets/images/pharma.jpg" alt="Pharma & Life Sciences" />
            </div>
          </div>
        </div>
      </section>

      {/* =====================================================
          SECTION 02B — VALUE CHAIN (BATCH) — RADIAL LAYOUT
      ===================================================== */}
      <section className="fashion-section radial-section" id="value-chain">
        <div className="fashion-section-inner">
          <div className="radial-plm">
            <div className="radial-info reveal-item">
              <div className="section-heading">
                <h2>Batch at the Heart of Everything</h2>
                <p>
                  From molecule to market. A connected product and business
                  lifecycle.
                </p>
              </div>
            </div>

            <div className="radial-diagram">
              <div className="radial-col radial-col-left">
                {radialLeftSteps.map((step, index) => (
                  <div
                    className="radial-card reveal-item"
                    key={step.title}
                    style={{
                      "--step-color": step.color,
                      "--delay": `${index * 70}ms`,
                    }}
                  >
                    <div className="radial-card-icon">
                      <Icon type={step.icon} />
                    </div>
                    <div className="radial-card-text">
                      <h3>{step.title}</h3>
                      <p>{step.description}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="radial-hub reveal-item">
                <div className="radial-hub-icon">
                  <Icon type="quality" />
                </div>
                <small>BATCH</small>
                <strong>The Connected Thread</strong>
                <span>
                  Material • Equipment • Process
                  <br />
                  Quality • Release • CAPA
                  <br />
                  Batch • Genealogy • Trace
                  <br />
                  Inventory • Expiry • Compliance
                </span>
              </div>

              <div className="radial-col radial-col-right">
                {radialRightSteps.map((step, index) => (
                  <div
                    className="radial-card radial-card-right reveal-item"
                    key={step.title}
                    style={{
                      "--step-color": step.color,
                      "--delay": `${index * 70}ms`,
                    }}
                  >
                    <div className="radial-card-icon">
                      <Icon type={step.icon} />
                    </div>
                    <div className="radial-card-text">
                      <h3>{step.title}</h3>
                      <p>{step.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* =====================================================
          SECTION 02C — INTELLIGENCE
      ===================================================== */}
      <section className="fashion-section intelligence-section">
        <div className="fashion-section-inner">
          <div className="section-heading reveal-item">
            <h2>Six Pharma & Wellness Intelligence Areas</h2>
            <p>End-to-end visibility. Measurable outcomes.</p>
          </div>

          <div className="intelligence-grid">
            {intelligenceAreas.map((area, index) => (
              <article
                className="intelligence-card reveal-card"
                key={area.title}
                style={{ "--delay": `${index * 70}ms` }}
              >
                <div className="intelligence-icon">
                  <Icon type={area.icon} />
                </div>

                <div>
                  <h3>{area.title}</h3>
                  <ul className="intelligence-points">
                    {area.items
                      .split(/\n|\s*•\s*/)
                      .filter(Boolean)
                      .map((point) => (
                        <li key={point}>{point}</li>
                      ))}
                  </ul>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* =====================================================
          SECTION 03 — WORKED QUESTION
      ===================================================== */}
      <section className="fashion-section worked-section">
        <div className="fashion-section-inner">
          <div className="section-heading reveal-item">
            <h2>A Worked Question</h2>
            <p>“Which inventory is likely to expire before it sells?”</p>
          </div>

          <div className="worked-layout">
            <div className="matrix-card reveal-item">
              <span className="matrix-tick matrix-tick-top">High</span>
              <span className="matrix-tick matrix-tick-bottom">Low</span>
              <div className="matrix-y">Sales Velocity</div>

              <div className="matrix">
                <div className="matrix-quadrant replenish">
                  <div className="matrix-quadrant-card">
                    <div className="matrix-quadrant-icon">
                      <Icon type="cart" />
                    </div>
                    <strong>Prioritise</strong>
                    <span>Sell before expiry</span>
                  </div>
                </div>

                <div className="matrix-quadrant star">
                  <div className="matrix-quadrant-card">
                    <div className="matrix-quadrant-icon">
                      <Icon type="box" />
                    </div>
                    <strong>Replenish</strong>
                    <span>Keep stock flowing</span>
                  </div>
                </div>

                <div className="matrix-quadrant stop">
                  <div className="matrix-quadrant-card">
                    <div className="matrix-quadrant-icon">
                      <Icon type="tag" />
                    </div>
                    <strong>Stop / Markdown</strong>
                    <span>Reduce exposure</span>
                  </div>
                </div>

                <div className="matrix-quadrant watch">
                  <div className="matrix-quadrant-card">
                    <div className="matrix-quadrant-icon">
                      <Icon type="chart" />
                    </div>
                    <strong>Monitor</strong>
                    <span>Track closely</span>
                  </div>
                </div>
              </div>

              <div className="matrix-x-row">
                <span className="matrix-tick">Low</span>
                <span className="matrix-x">Days to Expiry</span>
                <span className="matrix-tick">High</span>
              </div>
            </div>

            <div className="investigate-card reveal-item">
              <span className="mini-label">INVESTIGATE BY</span>

              <div className="investigate-list">
                <span><Icon type="database" /> Batch</span>
                <span><Icon type="box" /> Material</span>
                <span><Icon type="gear" /> Equipment</span>
                <span><Icon type="truck" /> Location</span>
                <span><Icon type="store" /> Distributor</span>
                <span><Icon type="people" /> Customer</span>
                <span><Icon type="chart" /> Season</span>
                <span><Icon type="shield" /> Compliance</span>
              </div>

              <div className="decision-divider" />

              <span className="mini-label">THEN DECIDE</span>

              <div className="decision-list">
                <span>✓ Transfer</span>
                <span>✓ Prioritise</span>
                <span>✓ Markdown</span>
                <span>✓ Stop Buying</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* =====================================================
          SECTION 04 — TIME BUSINESS
      ===================================================== */}
      <section className="fashion-section time-section">
        <div className="fashion-section-inner">
          <div className="section-heading reveal-item">
            <h2>Pharma Is a Time-Critical Business</h2>
            <p>The value of the decision falls as time passes.</p>
          </div>

          <div className="time-layout">
            <div className="time-line">
              {timeDecisions.map(([title, text, icon], index) => (
                <div
                  className="time-item reveal-item"
                  key={title}
                  style={{ "--delay": `${index * 65}ms` }}
                >
                  <span className="time-number">
                    {String(index + 1).padStart(2, "0")}
                  </span>

                  <div className="time-icon">
                    <Icon type={icon} />
                  </div>

                  <div className="time-copy">
                    <strong>{title}</strong>
                    <span>{text}</span>
                  </div>
                </div>
              ))}
            </div>

            <div className="decision-value reveal-item">
              <img
                src="/assets/images/pharma.jpg"
                alt="Pharma manufacturing"
                className="decision-value-image"
              />

              <div className="decision-value-overlay">
                <span className="decision-value-bar" aria-hidden="true" />
                <p>
                  Value of the decision
                  <br />
                  falls <span>as time passes</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* =====================================================
          SECTION 05 — ENTERPRISE SYSTEMS + CTA
      ===================================================== */}
      <section className="fashion-section enterprise-section">
        <div className="enterprise-layout">
          <div className="systems-card reveal-item">
            <div className="section-heading">
              <h2>
                Enterprise Systems <span>& Data</span>
              </h2>
              <p>
                Bring data together. Add pharma context. Deliver
                intelligence.
              </p>
            </div>

            <div className="systems-grid">
              {systems.map((system, index) => (
                <div
                  className={`system-box reveal-card ${
                    index === 0 ? "system-box-primary" : ""
                  }`}
                  key={system.name}
                  style={{ "--delay": `${index * 45}ms` }}
                >
                  <Icon type={system.icon} />
                  <span className="system-box-label">
                    <strong>{system.name}</strong>
                  </span>
                </div>
              ))}
            </div>

            <div className="data-boxes">
              <div className="reveal-item">
                <Icon type="database" />
                <div className="data-box-text">
                  <strong>Trusted Pharma Data</strong>
                  <span>
                    Transactional | Operational | Quality | Regulatory |
                    External
                  </span>
                </div>
              </div>

              <div className="reveal-item">
                <Icon type="layers" />
                <div className="data-box-text">
                  <strong>Pharma Semantics & Context</strong>
                  <span>
                    Formulation | Manufacturing | Inventory | Channel |
                    Customer
                  </span>
                </div>
              </div>

              <div className="reveal-item">
                <Icon type="flask" />
                <div className="data-box-text">
                  <strong>Pharma Intelligence</strong>
                  <span>Analytics | AI | Scenarios | Recommendations</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="final-cta" id="contact">
          <div className="final-cta-inner">
            <div>
              <h2>Every Batch Has a Business Story.</h2>
            </div>

            <a href="/tellusyourproblem#tell-details" className="btn btn-primary">
              Tell Us Your Problem
              <Icon type="arrowRight" />
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}
